import { Controller } from '@hotwired/stimulus';
import { includes, keys, pickBy } from 'lodash';
import {
  batchSetNodeKVPair,
  executeActionsInDom,
  updateDocumentUserState,
  waitForVisible,
  extractHiddenAnswerCommentHTML,
  restoreAnswerCommentHTML,
  shouldPreserveAnswerCommentInDom,
} from '../utils/case_experience_in_rails';

export default class extends Controller {
  static targets = ['gradedBanner', 'select', 'result'];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    inactive: Boolean,
    nodeState: Object, // State in DocumentUserState for this node
    nodeData: Object, // Original node data in Versions table
  };

  loadNodeData() {
    // Load nodeData from data attribute and remove it from DOM for security
    const nodeDataAttr = this.element.getAttribute('data-category-matcher-node-data-value');
    if (nodeDataAttr) {
      try {
        this._nodeData = JSON.parse(nodeDataAttr);
      } catch (error) {
        console.error('Error parsing nodeData JSON:', error);
        this._nodeData = {};
      }
      this.element.removeAttribute('data-category-matcher-node-data-value');
    } else {
      this._nodeData = {};
    }
    // Clear memoized values when nodeData changes
    this._categoryItems = undefined;
    this._categoryCategories = undefined;
    this._answerFromNodeData = undefined;
  }

  connect() {
    this.loadNodeData();
    // Store reference to submitButtonContainer if present
    const submitButtonContainer = this.element?.nextElementSibling;
    if (submitButtonContainer?.classList?.contains('doc-button-slice')) {
      this.submitButtonContainer = submitButtonContainer;
    } else {
      this.submitButtonContainer = null;
    }

    waitForVisible(this.element, () => {
      this.initializeCategoryMatcher();
    });
  }

  storeAnswerCommentHTML() {
    const result = extractHiddenAnswerCommentHTML(this.element);
    if (result) {
      this._answerCommentSibling = result.sibling;
      this.answerCommentHtml = result.html;
    } else {
      this._answerCommentSibling = null;
      this.answerCommentHtml = null;
    }
  }

  disconnect() {
    // Remove submit event listener if present
    if (this.submitButtonContainer && this.submitButton && this._handleSubmit) {
      this.submitButton.removeEventListener('click', this._handleSubmit);
    }
    this.submitButton = null;
    this._handleSubmit = null;
    this.submitButtonContainer = null;

    // Restore answer comment HTML on disconnect
    this.restoreAnswerCommentHTMLFromVariable();
  }

  restoreAnswerCommentHTMLFromVariable() {
    if (this.answerCommentHtml) {
      restoreAnswerCommentHTML(this.element, this.answerCommentHtml);
    }
  }

  initializeCategoryMatcher() {
    const graded = this.nodeStateValue?.graded?.value || false;
    this.categoryItems = this.getCategoryItems();
    if (graded) {
      // Disable all select targets
      this.disableSelectTargets();
      // Display graded banner
      this.gradedBannerTarget.innerHTML = this.getGradedBannerHtmlContent(
        this.getGradedBannerContent(),
      );
      // Display correct answer with appropriate styling
      this.displayCorrectAnswers();

      // Restore answer comment HTML after displaying graded feedback
      this.restoreAnswerCommentHTMLFromVariable();
    } else {
      if (!shouldPreserveAnswerCommentInDom()) {
        this.storeAnswerCommentHTML();
      }

      // Submit buttons are enabled only when not graded as per requirements
      this.enableSubmitButton();
      this.attachSubmitListener();
    }
  }

  enableSubmitButton() {
    if (this.submitButtonContainer) {
      // Update data-button-action-inactive-value to "1"
      this.submitButtonContainer.setAttribute('data-button-action-inactive-value', '1');
      const submitButton = this.submitButtonContainer.querySelector('button');
      if (submitButton) {
        this.submitButton = submitButton;
        this.submitButton.disabled = false;
        // update aria-disabled to false
        this.submitButton.setAttribute('aria-disabled', 'false');
      }
    }
  }

  attachSubmitListener() {
    if (this.submitButtonContainer) {
      if (this.submitButton) {
        this._handleSubmit = this.handleSubmit.bind(this);
        this.submitButton.addEventListener('click', this._handleSubmit);
      }
    }
  }

  async handleSubmit() {
    try {
      const onClickActions = this.submitButtonContainer.getAttribute(
        'data-button-action-on-click-value',
      );
      if (onClickActions) {
        const actions = JSON.parse(onClickActions);
        const response = await batchSetNodeKVPair(actions, this.documentIdValue, this.userIdValue);
        if (response && this.application) {
          this.nodeStateValue = response.state[this.nodeUidValue] || {};
          // Clear memoized value when nodeState changes
          this._valueFromState = undefined;
          executeActionsInDom(actions, this.application);
        }
      }
      this.initializeCategoryMatcher();

      // Restore answer comment HTML after submit
      this.restoreAnswerCommentHTMLFromVariable();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  }

  disableSelectTargets() {
    this.inactiveValue = true;
    this.selectTargets.forEach((select) => {
      select.disabled = true;
      select.setAttribute('aria-disabled', 'true');
    });
  }

  getSelectedOptionText(selectElement) {
    if (!selectElement) return '';
    const option = selectElement.options[selectElement.selectedIndex];
    return option && option.text ? option.text.replace(/\s+/g, ' ').trim() : '';
  }

  displayCorrectAnswers() {
    this.resultTargets?.forEach?.((result) => {
      const itemUid = result?.dataset?.itemUid;
      // Find the correct category UID for this item
      const answerUid = this.getCorrectCategoryUidForItem(itemUid);
      // Find the selected category UID for this item
      const selectElement = this.selectTargets?.find?.(
        (select) => select?.dataset?.itemUid === itemUid,
      );
      const selectedCategoryUid = selectElement?.value;
      // Compare UIDs for correctness
      const isCorrect = answerUid && selectedCategoryUid && answerUid === selectedCategoryUid;
      // Get the display text for the correct answer (category content)
      const answerText =
        this.getCategoryCategories()?.find?.((cat) => cat?.uid === answerUid)?.content ?? '';
      const span = document.createElement('span');
      span.className = `doc-category-matcher-row-answer ${isCorrect ? 'correct' : 'incorrect'}`;
      span.textContent = answerText;
      result.innerHTML = '';
      result.appendChild(span);
    });
  }

  getCorrectCategoryUidForItem(itemUid) {
    const answer = this.getAnswerFromNodeData();
    return keys(pickBy(answer, (contentItemArray) => includes(contentItemArray, itemUid)))?.[0];
  }

  async selectCategory(event) {
    if (this.inactiveValue) return;

    const select = event.target;
    const itemUid = select.dataset.itemUid;
    const categoryUid = select.value;

    try {
      // Get current value mapping
      const currentValue = await this.getCurrentValue();

      // Remove item from all categories
      const newValue = {};
      Object.keys(currentValue).forEach((catUid) => {
        newValue[catUid] = currentValue[catUid].filter((iUid) => iUid !== itemUid);
      });

      // Add item to selected category
      if (categoryUid !== 'default') {
        if (!newValue[categoryUid]) {
          newValue[categoryUid] = [];
        }
        newValue[categoryUid].push(itemUid);
      }

      const body = {
        document_user_state: {
          state: {
            [this.nodeUidValue]: {
              value: {
                value: newValue,
                updatedAt: new Date().getTime(),
              },
            },
          },
        },
      };

      // Save to document user state
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body,
      });
    } catch (error) {
      console.error('Error in selectCategory:', error);
    }
  }

  getCurrentValue() {
    // Get current value from DOM
    const selects = this.selectTargets;
    const value = {};
    selects.forEach((select) => {
      const itemUid = select?.dataset?.itemUid;
      const categoryUid = select?.value;
      if (categoryUid && categoryUid !== 'default') {
        if (!value[categoryUid]) {
          value[categoryUid] = [];
        }
        value[categoryUid].push(itemUid);
      }
    });
    return value;
  }

  sanitizeClassName(className) {
    if (!className) return '';
    return String(className)
      .split(/\s+/)
      .map((token) => token.replace(/[^a-zA-Z0-9_-]/g, ''))
      .filter(Boolean)
      .join(' ');
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/`/g, '&#96;');
  }

  getGradedBannerHtmlContent({ containerClassName, className, text }) {
    const safeContainerClassName = this.sanitizeClassName(containerClassName);
    const safeClassName = this.sanitizeClassName(className);
    const safeText = this.escapeHtml(text);
    return `
      <div class="doc-graded-feedback-banner ${safeContainerClassName}">
        <span class="doc-graded-feedback-banner-icon ${safeClassName}"></span>
        <span class="doc-graded-feedback-banner-text">${safeText}</span>
      </div>
    `;
  }

  getValueFromState() {
    if (this._valueFromState !== undefined) return this._valueFromState;
    this._valueFromState = this.nodeStateValue?.value?.value || {};
    return this._valueFromState;
  }

  getAnswerFromNodeData() {
    if (this._answerFromNodeData !== undefined) return this._answerFromNodeData;
    this._answerFromNodeData = this._nodeData?.answer || {};
    return this._answerFromNodeData;
  }

  getGradedBannerContent() {
    const contentItemsExpanded = this.categoryItems.map((contentItem) => {
      const categoryItemValue =
        keys(
          pickBy(this.getValueFromState(), (contentItemArray) =>
            includes(contentItemArray, contentItem.uid),
          ),
        )[0] || 'default';

      const categoryItemAnswer = keys(
        pickBy(this.getAnswerFromNodeData(), (contentItemArray) =>
          includes(contentItemArray, contentItem.uid),
        ),
      )[0];

      const isCorrect = categoryItemValue === categoryItemAnswer;

      return {
        ...contentItem,
        categoryItemValue,
        categoryItemAnswer,
        isCorrect,
      };
    });

    if (contentItemsExpanded.every((item) => item.isCorrect)) {
      return this.getClassNamesAndTextForCategoryItem('correct');
    } else if (contentItemsExpanded.some((item) => item.isCorrect)) {
      return this.getClassNamesAndTextForCategoryItem('partial');
    } else {
      return this.getClassNamesAndTextForCategoryItem('incorrect');
    }
  }

  getClassNamesAndTextForCategoryItem(type) {
    switch (type) {
      case 'correct':
        return {
          containerClassName: 'correct',
          className: 'icon-star',
          text: 'CORRECT',
        };
      case 'partial':
        return {
          containerClassName: 'partial',
          className: 'icon-warning',
          text: 'PARTIALLY CORRECT',
        };
      case 'incorrect':
        return {
          containerClassName: 'incorrect',
          className: 'icon-warning',
          text: 'INCORRECT',
        };
      case 'no-single-answer':
        return {
          containerClassName: 'no-single-answer',
          className: 'icon-star',
          text: 'THERE IS NO SINGLE BEST ANSWER',
        };
      case 'no-grade':
      default:
        return {
          containerClassName: 'not-graded',
          className: 'icon-star',
          text: 'NOT GRADED',
        };
    }
  }

  getCategoryItems() {
    if (this._categoryItems !== undefined) return this._categoryItems;
    // Prefer _nodeData if available, fallback to nodeDataValue
    const nodeData = this._nodeData || this.nodeDataValue || {};
    this._categoryItems = nodeData.contentItems || [];
    return this._categoryItems;
  }

  getCategoryCategories() {
    if (this._categoryCategories !== undefined) return this._categoryCategories;
    // Prefer _nodeData if available, fallback to nodeDataValue
    const nodeData = this._nodeData || this.nodeDataValue || {};
    this._categoryCategories = nodeData.contentCategories || [];
    return this._categoryCategories;
  }
}
